import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cog, CheckCircle2, XCircle, ChevronDown, Droplets, Thermometer, Beaker, Gauge, Zap, Wind, Atom, Layers
} from 'lucide-react';
import type { ProcessSimulation, DischargeLimits } from '../types/calculator';

interface ProcessTrainOverviewProps {
  simulation: ProcessSimulation | undefined;
  limits: DischargeLimits;
}

const safeNum = (n: number | undefined | null) => (typeof n === 'number' ? n : 0);

export default function ProcessTrainOverview({ simulation, limits }: ProcessTrainOverviewProps) {
  const [hoveredStage, setHoveredStage] = useState<number | null>(null);
  const [expandedStage, setExpandedStage] = useState<number | null>(null);

  if (!simulation || simulation.stages.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8">
        <Cog className="w-8 h-8 text-slate-600 mx-auto mb-2" />
        Run assessment to see process train overview
      </div>
    );
  }

  const checkCompliance = (stage: ProcessSimulation['stages'][0]) => {
    const checks = [
      stage.tss <= limits.tss,
      stage.bod5 <= limits.bod5,
      stage.cod <= limits.cod,
      stage.oilGrease <= limits.oilGrease,
      stage.tds <= limits.tds,
      (stage.tkn ?? stage.totalNitrogen * 0.85) <= limits.tkn,
      stage.totalNitrogen <= limits.totalNitrogen,
      stage.totalPhosphorus <= limits.totalPhosphorus,
      stage.ph >= limits.phMin && stage.ph <= limits.phMax,
      stage.temperature <= limits.temperature,
      stage.conductivity <= limits.conductivity,
    ];
    return checks.every(Boolean);
  };

  const getStageColor = (stageName: string) => {
    if (stageName.includes('Equalization') || stageName.includes('Headworks') || stageName.includes('Pre-Treatment')) return { bg: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30', bgSoft: 'bg-cyan-500/10' };
    if (stageName.includes('DAF') || stageName.includes('API') || stageName.includes('Coag') || stageName.includes('Primary')) return { bg: 'bg-rb-brand', text: 'text-rb-brand', border: 'border-rb-brand/30', bgSoft: 'bg-rb-brand/10' };
    if (stageName.includes('Sediment') || stageName.includes('Lamella') || stageName.includes('Clarif')) return { bg: 'bg-slate-500', text: 'text-slate-400', border: 'border-slate-500/30', bgSoft: 'bg-slate-500/10' };
    if (stageName.includes('Filter') || stageName.includes('MMF') || stageName.includes('Sand') || stageName.includes('Cloth') || stageName.includes('GAC')) return { bg: 'bg-slate-500', text: 'text-slate-400', border: 'border-slate-500/30', bgSoft: 'bg-slate-500/10' };
    if (stageName.includes('Biological') || stageName.includes('Sludge') || stageName.includes('Activated') || stageName.includes('MBR') || stageName.includes('MBBR') || stageName.includes('SAF') || stageName.includes('ABR') || stageName.includes('UASB')) return { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30', bgSoft: 'bg-emerald-500/10' };
    if (stageName.includes('RO') || stageName.includes('UF') || stageName.includes('MF') || stageName.includes('Membrane') || stageName.includes('Forward') || stageName.includes('Distillation')) return { bg: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30', bgSoft: 'bg-cyan-500/10' };
    if (stageName.includes('Polish') || stageName.includes('UV') || stageName.includes('Disinfection')) return { bg: 'bg-rb-brand', text: 'text-rb-brand', border: 'border-rb-brand/30', bgSoft: 'bg-rb-brand/10' };
    if (stageName.includes('Thick') || stageName.includes('Press') || stageName.includes('Centrif') || stageName.includes('Dewater') || stageName.includes('Dryer') || stageName.includes('Digest')) return { bg: 'bg-red-500', text: 'text-red-400', border: 'border-red-500/30', bgSoft: 'bg-red-500/10' };
    return { bg: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30', bgSoft: 'bg-cyan-500/10' };
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h3 className="text-sm font-bold text-white">Process Train Overview</h3>
        <p className="text-xs text-slate-500">Stage-by-stage with key parameters. Hover for detail.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {simulation.stages.map((stage, i) => {
          const colors = getStageColor(stage.stageName);
          const isHovered = hoveredStage === i;
          const isCompliant = checkCompliance(stage);
          const isExpanded = expandedStage === i;

          return (
            <div
              key={i}
              className={`relative rounded-xl border p-4 cursor-pointer transition-all duration-300 flex flex-col ${
                isHovered
                  ? isCompliant ? 'border-emerald-400 shadow-lg shadow-emerald-500/10 z-10' : 'border-red-400 shadow-lg shadow-red-500/10 z-10'
                  : isCompliant ? 'border-slate-700 hover:border-emerald-500/30' : 'border-slate-700 hover:border-red-500/30'
              } bg-slate-800/30`}
              onMouseEnter={() => setHoveredStage(i)}
              onMouseLeave={() => setHoveredStage(null)}
              onClick={() => setExpandedStage(isExpanded ? null : i)}
            >
              <div className={`absolute -top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold border z-10 flex items-center gap-1 ${
                isCompliant ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
                {isCompliant ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {isCompliant ? 'PASS' : 'FAIL'}
              </div>

              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-lg ${colors.bgSoft} ${colors.border} border flex items-center justify-center`}>
                  <Cog className={`w-4 h-4 ${colors.text}`} />
                </div>
                <span className={`text-xs font-bold ${colors.text}`}>Stage {stage.stageNumber}</span>
              </div>

              <h4 className="text-sm font-bold text-white mb-1">{stage.stageName}</h4>
              <p className="text-[10px] text-slate-500 mb-2">{stage.equipment.map(e => e.name).join(' + ')}</p>

              <div className="mt-auto space-y-1">
                {[
                  { label: 'BOD', value: stage.bod5, unit: 'mg/L', limit: limits.bod5, color: 'text-cyan-400' },
                  { label: 'COD', value: stage.cod, unit: 'mg/L', limit: limits.cod, color: 'text-rb-brand' },
                  { label: 'TSS', value: stage.tss, unit: 'mg/L', limit: limits.tss, color: 'text-slate-300' },
                  { label: 'pH', value: stage.ph, unit: '', limitMin: limits.phMin, limitMax: limits.phMax, color: 'text-emerald-400' },
                ].map((param, pi) => {
                  const val = safeNum(param.value as number);
                  let pass = true;
                  if (param.limit !== undefined) pass = val <= param.limit;
                  if (param.limitMin !== undefined && param.limitMax !== undefined) pass = val >= param.limitMin && val <= param.limitMax;
                  return (
                    <div key={pi} className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{param.label}</span>
                      <span className={`font-mono font-bold ${pass ? param.color : 'text-red-400'}`}>
                        {val.toFixed(param.label === 'pH' ? 2 : 0)}{param.unit ? ` ${param.unit}` : ''}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-2 flex items-center justify-center">
                <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 pt-3 border-t border-slate-700 space-y-1.5">
                      {[
                        { label: 'TDS', value: stage.tds, unit: 'mg/L' },
                        { label: 'TKN', value: stage.tkn ?? (safeNum(stage.totalNitrogen) * 0.85), unit: 'mg/L' },
                        { label: 'Total N', value: stage.totalNitrogen, unit: 'mg/L' },
                        { label: 'Total P', value: stage.totalPhosphorus, unit: 'mg/L' },
                        { label: 'O&G', value: stage.oilGrease, unit: 'mg/L' },
                        { label: 'Conductivity', value: stage.conductivity, unit: 'µS/cm' },
                        { label: 'Temp', value: stage.temperature, unit: '°C' },
                      ].map((param, pi) => (
                        <div key={pi} className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">{param.label}</span>
                          <span className="font-mono text-slate-300">{safeNum(param.value as number).toFixed(0)}{param.unit ? ` ${param.unit}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
